/* One cup proof composition for downloads, saved designs and storefront previews. */
(() => {
  if (globalThis.EngravingPreview) return;
  function compose(sources) {
    if (!Array.isArray(sources) || !sources.length) throw new Error('preview_empty');
    const size = source => ({ width: source.naturalWidth || source.width, height: source.naturalHeight || source.height });
    const height = size(sources[0]).height;
    if (!height) throw new Error('preview_empty');
    if (sources.length === 1 && typeof sources[0].getContext === 'function') return sources[0];
    const views = sources.map(source => {
      const original = size(source);
      if (!original.width || !original.height) throw new Error('preview_empty');
      const isCanvas = typeof source.getContext === 'function';
      const canvas = isCanvas ? source : document.createElement('canvas');
      if (!isCanvas) { canvas.width = original.width; canvas.height = original.height; }
      const context = canvas.getContext('2d');
      if (!isCanvas) context.drawImage(source, 0, 0);
      let left = 0, right = canvas.width - 1;
      if (sources.length > 1) {
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        left = canvas.width; right = -1;
        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const index = (y * canvas.width + x) * 4;
            // Preserve even near-white cups and silver engraving. Never crop vertically.
            if (pixels[index + 3] && (pixels[index] < 255 || pixels[index + 1] < 255 || pixels[index + 2] < 255)) {
              left = Math.min(left, x); right = Math.max(right, x);
            }
          }
        }
        if (right < left) { left = 0; right = canvas.width - 1; }
      }
      return { canvas, left, width: right - left + 1, ratio: height / original.height };
    });
    const scale = height / 1000, gap = sources.length > 1 ? 24 * scale : 0;
    const viewsWidth = views.reduce((sum, view) => sum + view.width * view.ratio, gap * (views.length - 1));
    const output = document.createElement('canvas');
    output.width = sources.length > 1 ? Math.max(height, Math.ceil(viewsWidth + 40 * scale)) : viewsWidth;
    output.height = height;
    const context = output.getContext('2d');
    context.fillStyle = '#ffffff'; context.fillRect(0, 0, output.width, output.height);
    context.imageSmoothingEnabled = false;
    let x = Math.floor((output.width - viewsWidth) / 2);
    for (const view of views) {
      context.drawImage(view.canvas, view.left, 0, view.width, view.canvas.height, x, 0, view.width * view.ratio, height);
      x += view.width * view.ratio + gap;
    }
    return output;
  }
  globalThis.EngravingPreview = { compose };
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;

  function imageAt(url, cors = true) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      let settled = false;
      const finish = error => { if (settled) return; settled = true; clearTimeout(timer); image.onload = image.onerror = null; error ? reject(error) : resolve(image); };
      const timer = setTimeout(() => finish(new Error('preview_image')), 15000);
      if (cors) image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => {
        if (typeof image.decode === 'function') image.decode().then(() => finish(), () => finish(new Error('preview_image')));
        else finish();
      };
      image.onerror = () => finish(new Error('preview_image'));
      image.src = url;
    });
  }
  function uploadURL(value) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port && !url.username && !url.password && url.pathname.includes('/uploads/');
    } catch { return false; }
  }
  class StudioCupPreview extends HTMLElement {
    connectedCallback() {
      if (this.dataset.manual !== undefined || this.dataset.state === 'ready') return;
      const start = () => { this.observer?.disconnect(); this.load().catch(() => {}); };
      if (typeof IntersectionObserver === 'undefined') start();
      else {
        this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) start(); }, { rootMargin: '200px' });
        this.observer.observe(this);
      }
    }
    disconnectedCallback() { this.observer?.disconnect(); this.version = (this.version || 0) + 1; this.pending = null; }
    load() {
      if (this.dataset.state === 'ready') return Promise.resolve();
      if (this.pending) return this.pending;
      const version = this.version = (this.version || 0) + 1;
      const urls = [this.dataset.back, this.dataset.front];
      this.dataset.state = 'loading'; this.setAttribute('aria-busy', 'true');
      this.textContent = this.dataset.loading || '';
      const current = () => version === this.version;
      const work = async () => {
        if (!urls.every(uploadURL)) throw new Error('preview_url');
        const images = await Promise.all(urls.map(url => imageAt(url).catch(() => imageAt(url, false))));
        if (!current()) throw new Error('preview_stale');
        let result;
        try { result = compose(images); result.setAttribute('aria-hidden', 'true'); }
        catch (error) {
          if (error.name !== 'SecurityError') throw error;
          // A CDN without CORS still permits a readable paired preview inside one frame.
          result = document.createElement('span'); result.className = 'studio-cup-preview__fallback';
          images.forEach(image => { image.alt = ''; image.setAttribute('aria-hidden', 'true'); result.append(image); });
        }
        if (!current()) throw new Error('preview_stale');
        this.replaceChildren(result); this.dataset.state = 'ready'; this.setAttribute('aria-busy', 'false');
      };
      this.pending = work().catch(error => {
        if (current()) { this.dataset.state = 'error'; this.setAttribute('aria-busy', 'false'); this.textContent = this.dataset.error || ''; }
        throw error;
      }).finally(() => { if (current()) this.pending = null; });
      return this.pending;
    }
  }
  if (!customElements.get('studio-cup-preview')) customElements.define('studio-cup-preview', StudioCupPreview);
})();
